import { ChildrenProxy } from '@omnea-app/frontend/components/shared/util/ChildrenProxy'
import { AlertErrorFallback } from './components/ErrorFallbacks'
import { LoadingComponentErrorBoundary, LoadingComponentErrorBoundaryProps } from './components/LoadingComponentErrorBoundary'
import {
  LoadingComponentSuspenseBoundary,
  LoadingComponentSuspenseBoundaryProps,
} from './components/LoadingComponentSuspenseBoundary'
import { FullWidthLoadingFallback } from './components/LoadingFallbacks'
import type { ComponentType, FC } from 'react'

type AnyProps = Exclude<object, null>

export interface LoadingBoundaryProps<Props extends AnyProps> {
  /** Propagates to the next error boundary up the React tree */
  catchError?: boolean
  /** Ignored if `catchError` is `false` */
  errorFallback?: LoadingComponentErrorBoundaryProps<Props>['fallback']

  /** Propagates to the next suspense boundary up the React tree */
  catchLoading?: boolean
  /** Ignored if `catchLoading` is `false` */
  loadingFallback?: LoadingComponentSuspenseBoundaryProps<Props>['fallback']
}

export const loadingComponent = <Props extends AnyProps>(
  Main: ComponentType<Props>,
  config: LoadingBoundaryProps<Props> = {}
): FC<Props> => {
  const {
    catchError = true,
    errorFallback = catchError ? <AlertErrorFallback /> : null,

    catchLoading = true,
    loadingFallback = catchLoading ? <FullWidthLoadingFallback /> : null,
  } = config

  const ErrorBoundary = catchError ? LoadingComponentErrorBoundary : ChildrenProxy
  const SuspenseBoundary = catchLoading ? LoadingComponentSuspenseBoundary : ChildrenProxy

  const LoadingComponent: FC<Props> = (props) => (
    <ErrorBoundary fallback={errorFallback} mainComponentProps={props}>
      <SuspenseBoundary fallback={loadingFallback} mainComponentProps={props}>
        <Main {...props} />
      </SuspenseBoundary>
    </ErrorBoundary>
  )

  return LoadingComponent
}
